import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
} from "@angular/core";
import { AnimationEvent, animate, style, transition, trigger } from "@angular/animations";
import { Overlay, OverlayRef } from "@angular/cdk/overlay";
import { TemplatePortal } from "@angular/cdk/portal";

export interface SelectorBottomSheetOption {
  value: string | number;
  label: string;
  description?: string;
  disabled?: boolean;
}

@Component({
  selector: "ds-selector-bottom-sheet",
  templateUrl: "./ds-selector-bottom-sheet.component.html",
  styleUrls: ["./ds-selector-bottom-sheet.component.scss"],
  animations: [
    trigger("slideUp", [
      transition(":enter", [
        style({ transform: "translateY(100%)" }),
        animate("300ms ease-out", style({ transform: "translateY(0)" })),
      ]),
      transition(":leave", [animate("200ms ease-in", style({ transform: "translateY(100%)" }))]),
    ]),
    trigger("fadeIn", [
      transition(":enter", [style({ opacity: 0 }), animate("300ms ease-out", style({ opacity: 1 }))]),
      transition(":leave", [animate("200ms ease-in", style({ opacity: 0 }))]),
    ]),
  ],
})
export class DsSelectorBottomSheetComponent implements OnDestroy {
  @Input() label = "";
  @Input() placeholder = "";
  @Input() title = "";
  @Input() description = "";
  @Input() cancelLabel = "Cancelar";
  @Input() options: SelectorBottomSheetOption[] = [];
  @Input() value: string | number | null = null;
  @Input() disabled = false;
  @Output() valueChange = new EventEmitter<string | number>();

  isOpen = false;

  /**
   * Template projected into the CDK overlay (attached to <body>). Keeping the
   * overlay/panel inline inside the component would leave them trapped inside
   * ancestor stacking contexts (e.g. iOS Safari's `-webkit-overflow-scrolling: touch`
   * or any `transform` on parents), which causes them to render behind other
   * fixed UI like `ds-bottom-sheet`.
   */
  @ViewChild("panelTemplate", { static: true }) private panelTemplate!: TemplateRef<unknown>;
  private overlayRef?: OverlayRef;

  constructor(private overlay: Overlay, private viewContainerRef: ViewContainerRef) {}

  get selectedLabel(): string {
    return this.options.find((option) => option.value === this.value)?.label ?? "";
  }

  open(): void {
    if (this.disabled) {
      return;
    }

    if (!this.overlayRef) {
      this.overlayRef = this.overlay.create({
        positionStrategy: this.overlay.position().global(),
        scrollStrategy: this.overlay.scrollStrategies.block(),
        // The component renders its own backdrop (`.selector-bs__overlay`)
        // with a fade animation, so we skip the CDK-provided one.
        hasBackdrop: false,
      });
      this.overlayRef.attach(new TemplatePortal(this.panelTemplate, this.viewContainerRef));
    }

    this.isOpen = true;
  }

  close(): void {
    this.isOpen = false;
    // The OverlayRef is disposed in `onPanelAnimationDone` once the leave
    // animation finishes, so the slide-down still plays.
  }

  /**
   * Dispose the CDK overlay only after the leave animation completes. Without
   * this the overlay would be torn down synchronously when `isOpen` flips to
   * false and the slide-down/fade-out animations would never play.
   */
  onPanelAnimationDone(event: AnimationEvent): void {
    if (event.toState === "void" && !this.isOpen && this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = undefined;
    }
  }

  select(option: SelectorBottomSheetOption): void {
    if (option.disabled) {
      return;
    }
    if (option.value !== this.value) {
      this.valueChange.emit(option.value);
    }
    this.close();
  }

  trackByValue(_index: number, option: SelectorBottomSheetOption): string | number {
    return option.value;
  }

  ngOnDestroy(): void {
    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = undefined;
    }
  }
}
